import * as React from 'react'

import { TProductDetailState } from '~logic/xstate/machines/menu/productDetailMachineTypes'
import { menuScreenElementIds } from '~shared/config/menuScreenElementIds'
import { useCategoryElements, useCategoryObserver } from '~shared/segments/ProductList'
import { getSpacingValueInPx } from '~ui/styleGuide/spaces'

type TAddonCategory = TProductDetailState['addonCategories'][number]

type TOptions = {
  /**
   * known height of a sticky content
   */
  knownStickyContentHeightPx: number
  /**
   * used as a dependency to recalculate the intersection observer settings
   */
  addonCategories: RoA<TAddonCategory>
  /**
   * height of the sticky content = addon category navigation
   */
  stuckContentHeight: FirstParam<typeof useCategoryObserver>['stuckContentHeight']
}

const { addonListContainerId, addonStickyContentId, addonCategoryScrollContainerId } =
  menuScreenElementIds

/**
 * The hook is responsible for watching addon categories rendered inside of
 * the container with the given id `addonListContainerId`.
 * It returns currently selected category id, which is either
 * the category intersecting the viewport boundaries (specified by the observer options)
 * OR the category selected using `onSelection` handler.
 */
export const useAddonListSelection = ({
  addonCategories,
  stuckContentHeight,
  knownStickyContentHeightPx,
}: TOptions) => {
  const [selectedId, setSelectedId] = React.useState<Nullable<ID>>(null)

  const refListElements = useCategoryElements({
    dependencyData: addonCategories,
    containerId: addonListContainerId,
  })

  useCategoryObserver({
    refListElements,
    dependencyData: addonCategories,
    onCategoryScrolledTo: setSelectedId,
    stuckContentHeight,
  })

  const scrollToCategory = React.useCallback(
    (selectedId: ID) => {
      const dynamicStuckContent = document.getElementById(addonStickyContentId)
      const scrollingContainer = document.getElementById(addonCategoryScrollContainerId) // not the entire window is scrolling, but only an element (overflow: auto)
      const categoryElement = refListElements.current.find((element) => element.id === selectedId)

      if (!scrollingContainer) {
        return
      }

      const categoryOffset = categoryElement?.offsetTop ?? 0
      const dynamicStuckContentHeight = dynamicStuckContent?.clientHeight ?? 0
      const stuckContentHeight = dynamicStuckContentHeight + knownStickyContentHeightPx
      const scrollPadding = getSpacingValueInPx(`medium`)
      const scrollPosition = categoryOffset - stuckContentHeight - scrollPadding

      scrollingContainer.scrollTo({ top: scrollPosition })
    },
    [knownStickyContentHeightPx, refListElements],
  )

  const onSelection = React.useCallback(
    (selectedId: ID) => {
      setSelectedId(selectedId)
      scrollToCategory(selectedId)
    },
    [scrollToCategory],
  )

  return {
    selectedId,
    onSelection,
  }
}
